import React, { useState, useRef, useEffect } from 'react';
import styled, { keyframes } from 'styled-components';
import { motion, useScroll, useTransform } from 'framer-motion';
import { Canvas, useFrame, useThree } from '@react-three/fiber';
import { Float, OrbitControls, Text3D, Sphere, Box, Torus, Cylinder, Octahedron } from '@react-three/drei';
import * as THREE from 'three';

// 3D Components for Next Section
const NextSection3DElements = () => {
  const { scene } = useThree();

  useEffect(() => {
    scene.fog = new THREE.Fog('#0a0a0a', 1, 25);
  }, [scene]);

  return (
    <>
      <ambientLight intensity={0.4} />
      <directionalLight position={[10, 10, 5]} intensity={1.2} />
      <pointLight position={[-10, -10, -5]} intensity={0.6} color="#FF6B35" />
      <pointLight position={[10, -10, -5]} intensity={0.6} color="#4a90e2" />
      <pointLight position={[0, 10, -5]} intensity={0.4} color="#8b5cf6" />

      <Float speed={1.8} rotationIntensity={0.6} floatIntensity={0.6}>
        <mesh position={[-5, 3, 0]}>
          <sphereGeometry args={[1, 32, 32]} />
          <meshStandardMaterial
            color="#FF6B35"
            transparent
            opacity={0.6}
            emissive="#FF6B35"
            emissiveIntensity={0.3}
          />
        </mesh>
      </Float>

      <Float speed={2.2} rotationIntensity={0.4} floatIntensity={0.4}>
        <mesh position={[5, -2, 2]}>
          <boxGeometry args={[1.5, 1.5, 1.5]} />
          <meshStandardMaterial
            color="#4a90e2"
            transparent
            opacity={0.6}
            emissive="#4a90e2"
            emissiveIntensity={0.3}
          />
        </mesh>
      </Float>

      <Float speed={1.5} rotationIntensity={0.5} floatIntensity={0.5}>
        <mesh position={[0, 4, -3]}>
          <torusGeometry args={[1.2, 0.4, 16, 100]} />
          <meshStandardMaterial
            color="#8b5cf6"
            transparent
            opacity={0.6}
            emissive="#8b5cf6"
            emissiveIntensity={0.3}
          />
        </mesh>
      </Float>

      <Float speed={2.5} rotationIntensity={0.7} floatIntensity={0.7}>
        <mesh position={[-3, -3, 1]}>
          <cylinderGeometry args={[0.8, 0.8, 1.5, 32]} />
          <meshStandardMaterial
            color="#fbbf24"
            transparent
            opacity={0.6}
            emissive="#fbbf24"
            emissiveIntensity={0.3}
          />
        </mesh>
      </Float>

      <Float speed={1.9} rotationIntensity={0.5} floatIntensity={0.5}>
        <mesh position={[3, 2, -1]}>
          <octahedronGeometry args={[0.8]} />
          <meshStandardMaterial
            color="#22d3ee"
            transparent
            opacity={0.6}
            emissive="#22d3ee"
            emissiveIntensity={0.3}
          />
        </mesh>
      </Float>

      <Float speed={2.1} rotationIntensity={0.6} floatIntensity={0.6}>
        <mesh position={[-2, 1, 3]}>
          <sphereGeometry args={[0.7, 32, 32]} />
          <meshStandardMaterial
            color="#ec4899"
            transparent
            opacity={0.6}
            emissive="#ec4899"
            emissiveIntensity={0.3}
          />
        </mesh>
      </Float>

      <Float speed={1.7} rotationIntensity={0.4} floatIntensity={0.4}>
        <mesh position={[2, -1, -2]}>
          <boxGeometry args={[1.2, 1.2, 1.2]} />
          <meshStandardMaterial
            color="#10b981"
            transparent
            opacity={0.6}
            emissive="#10b981"
            emissiveIntensity={0.3}
          />
        </mesh>
      </Float>
    </>
  );
};

// Styled Components
const NextSectionContainer = styled.section`
  width: 100%;
  min-height: 100vh;
  background: linear-gradient(135deg, #0a0a0a 0%, #1a1a1a 50%, #0a0a0a 100%);
  position: relative;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 15px;
  margin-bottom:15px;
`;

const CanvasContainer = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 1;
`;

const ContentOverlay = styled.div`
  position: relative;
  z-index: 10;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ContentWrapper = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 20px;
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
  z-index: 2;
`;

// ✅ Make it a normal h2 (not motion.h2)
const SectionTitle = styled.h2`
margin-top:50px;
  font-size: 3.2rem;
  color: #ffffff;
  margin-bottom: 30px;
  text-align: center;
  font-family: 'Manrope', sans-serif;
  font-weight: 700;
  background: linear-gradient(45deg, #FF6B35, #4a90e2);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  text-shadow: 0 0 30px rgba(255, 107, 53, 0.3);
  
  @media (max-width: 768px) {
    font-size: 2.5rem;
  }
`;

const SectionSubtitle = styled.p`
  font-size: 1.2rem;
  color: #cccccc;
  margin-bottom: 60px;
  text-align: center;
  max-width: 1000px;
  line-height: 1.8;
  font-family: 'Roboto', sans-serif;

  @media (max-width: 768px) {
    font-size: 1.1rem;
    margin-bottom: 40px;
  }
`;

const ServicesGrid = styled(motion.div)`
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  grid-template-rows: repeat(2, 1fr);
  gap: 25px;
  width: 100%;
  margin-top: 40px;
  
  @media (max-width: 1100px) {
    grid-template-columns: repeat(2, 1fr);
    grid-template-rows: repeat(5, 1fr);
  }
  @media (max-width: 700px) {
    grid-template-columns: 1fr;
    grid-template-rows: none;
  }
`;

const ServiceCard = styled(motion.div)`
  background: rgba(26, 26, 26, 0.8);
  backdrop-filter: blur(15px);
  border-radius: 20px;
  padding: 35px 25px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  position: relative;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  min-width: 200px;
  min-height: 180px;
  max-width: 240px;
  max-height: 200px;
  margin: 0 auto;
  text-align: center;
  cursor: pointer;
  transition: all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275);
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(45deg, rgba(255, 107, 53, 0.1), rgba(74, 144, 226, 0.1));
    opacity: 0;
    transition: opacity 0.3s ease;
  }
  
  &:hover {
    transform: translateY(-15px) scale(1.05);
    border-color: rgba(255, 107, 53, 0.4);
    box-shadow: 0 25px 50px rgba(255, 107, 53, 0.3);
    
    &::before {
      opacity: 1;
    }
  }
  
  @media (max-width: 700px) {
    max-width: 100%;
    min-width: 0;
  }
`;

const ServiceIcon = styled.div`
  font-size: 2rem;
  margin-bottom: 20px;
  text-align: center;
  background: linear-gradient(45deg, #FF6B35, #4a90e2);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
`;

const ServiceTitle = styled.h3`
  font-size: 1.5rem;
  color: #ffffff;
  margin-bottom: 12px;
  text-align: center;
  font-family: 'Roboto', sans-serif;
  font-weight: 600;
`;

const ServiceDescription = styled.p`
  font-size: 0.60rem;
  color: #cccccc;
  line-height: 1.6;
  text-align: center;
  font-family: 'Roboto', sans-serif;
  margin-bottom: 0;
`;

const NextSection = () => {
  const containerRef = useRef();
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start end", "end start"]
  });

  const titleY = useTransform(scrollYProgress, [0, 1], [100, -100]);
  const subtitleY = useTransform(scrollYProgress, [0, 1], [50, -50]);
  const gridY = useTransform(scrollYProgress, [0, 1], [200, -200]);

  const cardVariants = {
    hidden: { opacity: 0, y: 50, scale: 0.8 },
    visible: (i) => ({
      opacity: 1,
      y: 0,
      scale: 1,
      transition: {
        duration: 0.8,
        delay: i * 0.1,
        ease: [0.25, 0.4, 0.25, 1],
      },
    }),
  };

  const services = [
    {
      icon: "📱",
      title: "Android Management",
      description: "Comprehensive Android device management solutions for enterprise security and productivity."
    },
    {
      icon: "💻",
      title: "Software Development",
      description: "Custom software solutions tailored to your business needs and requirements."
    },
    {
      icon: "🛠️",
      title: " IT Support ",
      description: "24/7 technical support and maintenance services for your IT infrastructure."
    },
    {
      icon: "🍎",
      title: "iOS Management",
      description: "Secure and efficient iOS device management for enterprise environments."
    },
    {
      icon: "🌐",
      title: "Web Development",
      description: "Modern web applications and responsive websites built with cutting-edge technologies."
    },
    {
      icon: "☁️",
      title: "Cloud Computing",
      description: "Scalable cloud solutions for storage, computing, and application hosting."
    },
    {
      icon: "📈",
      title: "Digital Marketing",
      description: "Strategic digital marketing campaigns to boost your online presence and growth."
    },
    {
      icon: "🔒",
      title: "Cyber Security",
      description: "Advanced security solutions to protect your data and systems from threats."
    },
    {
      icon: "📲",
      title: "Mobile Management",
      description: "Comprehensive mobile device management across all platforms and devices."
    },
    {
      icon: "💡",
      title: "IT Consulting",
      description: "Expert IT consulting services to optimize your technology strategy and implementation."
    }
  ];

  return (
    <NextSectionContainer ref={containerRef}>
      <CanvasContainer>
        <Canvas camera={{ position: [0, 0, 10], fov: 75 }}>
          <NextSection3DElements />
          <OrbitControls
            enableZoom={false}
            enablePan={false}
            autoRotate
            autoRotateSpeed={0.4}
            maxPolarAngle={Math.PI / 2}
            minPolarAngle={Math.PI / 2}
          />
        </Canvas>
      </CanvasContainer>

      <ContentOverlay>
        <ContentWrapper>
          <SectionTitle>
            {/* style={{ y: titleY }} */}
            {/* initial={{ opacity: 0, y: 50 }}
             whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 1 }}
             */}
            Our Comprehensive Services
          </SectionTitle>

          <SectionSubtitle>
            {/* style={{ y: subtitleY }}
          initial={{ opacity: 0, y: 30 }}
           whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.2 }} */}

            Discover our full range of IT services designed to transform your business. From mobile device management to cloud computing, we provide cutting-edge solutions that drive innovation and growth. Our expert team ensures seamless integration and exceptional support for all your technology needs.
          </SectionSubtitle>

          <ServicesGrid
            style={{ y: gridY }}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
          >
            {services.map((service, index) => (
              <ServiceCard
                key={index}
                custom={index}
                variants={cardVariants}
                whileHover={{ scale: 1.05, y: -10 }}
                whileTap={{ scale: 0.95 }}
              >
                <ServiceIcon>{service.icon}</ServiceIcon>
                <ServiceTitle>{service.title}</ServiceTitle>
                <ServiceDescription>{service.description}</ServiceDescription>
              </ServiceCard>
            ))}
          </ServicesGrid>
        </ContentWrapper>
      </ContentOverlay>
    </NextSectionContainer >
  );
};

export default NextSection; 